'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { cloneElement, Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { BATTLESHIP, GYPSUM } from 'HubStyleTokens/colors';
import FormattedCurrency from 'I18n/components/FormattedCurrency';
import FormattedMessage from 'I18n/components/FormattedMessage';
import Small from 'UIComponents/elements/Small';
import UIBadge from 'UIComponents/badge/UIBadge';
import UICardHero from 'UIComponents/card/UICardHero';
import UICardSection from 'UIComponents/card/UICardSection';
import UICardWrapper from 'UIComponents/card/UICardWrapper';
import AbstractTextElement from 'UIComponents/elements/AbstractTextElement';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIImage from 'UIComponents/image/UIImage';
import UIFlex from 'UIComponents/layout/UIFlex';
import { STAR_RATING_FILLED } from '../../constants/reviews/RatingTypes';
import { MINIMUM_REVIEWS_FOR_DISPLAY } from '../../constants/reviews/ReviewsListQueries';
import { formatCentsToDollars } from '../../utils/currency';
import Rating from '../reviews/Rating';
import * as ScreenSizes from '../../constants/layout/ScreenSizes';
import { useResponsiveProps } from '../../hooks/useResponsiveProps';
const THUMBNAIL_PLACEHOLDER_FLEX_CONTAINER_CLASS = 'theme-card__thumbnail-placeholder__flex';
const ThumbnailPlaceholder = styled(props => /*#__PURE__*/_jsx("div", Object.assign({}, props, {
  children: /*#__PURE__*/_jsx(UIFlex, {
    className: THUMBNAIL_PLACEHOLDER_FLEX_CONTAINER_CLASS,
    align: "center",
    justify: "center",
    children: /*#__PURE__*/_jsx(UIIcon, {
      color: BATTLESHIP,
      name: "insertImage",
      size: 120
    })
  })
}))).withConfig({
  displayName: "ThemeCard__ThumbnailPlaceholder",
  componentId: "sc-8xtvee-0"
})(["position:relative;width:100%;padding-top:75%;background-color:", ";border-top-left-radius:inherit;border-top-right-radius:inherit;overflow:hidden;.", "{position:absolute;top:0;left:0;bottom:0;right:0;}"], GYPSUM, THUMBNAIL_PLACEHOLDER_FLEX_CONTAINER_CLASS);
ThumbnailPlaceholder.displayName = 'ThumbnailPlaceholder';
const StyledPrice = styled(AbstractTextElement).withConfig({
  displayName: "ThemeCard__StyledPrice",
  componentId: "sc-8xtvee-1"
})(["font-size:20px;"]);
StyledPrice.displayName = 'StyledPrice';

const ThemeCard = ({
  className,
  companyName,
  detailsButton,
  demoButton,
  name,
  priceCents,
  providerName,
  reviewSummary,
  thumbnailUrl
}) => {
  const {
    isMobileView
  } = useResponsiveProps({
    isMobileView: {
      [ScreenSizes.XS]: true,
      [ScreenSizes.SM]: false
    }
  });

  const renderedTitleCompanyOffering = /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx("div", {
      className: "is--heading-4 m-bottom-0",
      children: name
    }), /*#__PURE__*/_jsx("div", {
      children: /*#__PURE__*/_jsx(Small, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "ecosystemMarketplaceCore.cards.themeCard.companyName",
          options: {
            companyName: providerName || companyName || ''
          }
        })
      })
    }), /*#__PURE__*/_jsx("div", {
      className: "m-y-2",
      children: /*#__PURE__*/_jsx(UIBadge, {
        use: "koala",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "ecosystemMarketplaceCore.cards.themeCard.offeringType"
        })
      })
    })]
  });

  const renderedReviewSummary = reviewSummary && reviewSummary.reviewCount >= MINIMUM_REVIEWS_FOR_DISPLAY ? /*#__PURE__*/_jsx(Rating, {
    rating: reviewSummary.overallRating,
    label: reviewSummary.reviewCount,
    use: "card",
    type: STAR_RATING_FILLED
  }) : /*#__PURE__*/_jsx("span", {});
  const renderedPrice = priceCents != null ? /*#__PURE__*/_jsx(StyledPrice, {
    weight: "medium",
    className: "p-x-2",
    children: priceCents === 0 ? /*#__PURE__*/_jsx(FormattedMessage, {
      message: "ecosystemMarketplaceCore.cards.themeCard.planType.free"
    }) : /*#__PURE__*/_jsx(FormattedCurrency, {
      currencyCode: "USD",
      simplifyCurrency: true,
      value: formatCentsToDollars(priceCents)
    })
  }) : null;
  return /*#__PURE__*/_jsxs(UICardWrapper, {
    className: className,
    compact: true,
    children: [thumbnailUrl ? /*#__PURE__*/_jsx(UICardHero, {
      children: /*#__PURE__*/_jsx(UIImage, {
        width: "100%",
        src: thumbnailUrl
      })
    }) : /*#__PURE__*/_jsx(ThumbnailPlaceholder, {}), /*#__PURE__*/_jsx(UICardSection, {
      scrollable: true,
      children: /*#__PURE__*/_jsxs(UIFlex, {
        direction: "row",
        align: "end",
        justify: "between",
        children: [/*#__PURE__*/_jsxs(UIFlex, {
          direction: "column",
          children: [renderedTitleCompanyOffering, renderedReviewSummary]
        }), /*#__PURE__*/_jsxs(UIFlex, {
          direction: "column",
          align: "end",
          children: [renderedPrice, (detailsButton || demoButton) && /*#__PURE__*/_jsxs(UIFlex, {
            direction: "row",
            align: "center",
            className: "p-top-2 p-left-2",
            wrap: isMobileView ? 'wrap' : 'nowrap',
            children: [detailsButton && /*#__PURE__*/cloneElement(detailsButton, {
              size: 'small'
            }), demoButton && /*#__PURE__*/cloneElement(demoButton, {
              size: 'small'
            })]
          })]
        })]
      })
    })]
  });
};

ThemeCard.propTypes = {
  className: PropTypes.string,
  companyName: PropTypes.string,
  demoButton: PropTypes.element,
  detailsButton: PropTypes.element,
  name: PropTypes.string,
  priceCents: PropTypes.number,
  providerName: PropTypes.string,
  reviewSummary: PropTypes.shape({
    overallRating: PropTypes.number.isRequired,
    reviewCount: PropTypes.number.isRequired
  }),
  thumbnailUrl: PropTypes.string
};
export default ThemeCard;